import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
    en: {
        translation: {
            // General
            welcome: "Welcome",
            bookstore: "Bookik",
            home: "Home",
            aboutUs: "About Us",
            
            // About Us Page
            giveBooksNewLife: "Give Your Books a New Home",
            missionStatement: "We believe in making books accessible to everyone while giving your old books a second chance. Send us your pre-loved books, and we'll ensure they find new homes.",
            ourImpact: "Our Impact",
            booksShared: "Books Received",
            activeCommunity: "Happy Customers",
            treeSaved: "Trees Saved",
            howItWorks: "How It Works",
            sustainabilityFeature: "Easy Book Donation",
            sustainabilityFeatureDescription: "Have books you no longer need? Send them to us easily through the Bookik app—we even cover the delivery cost!",
            communityFeature: "Earn From Donations",
            communityFeatureDescription: "Get 1zł for each book you donate to us",
            accessibilityFeature: "Quality Check",
            accessibilityFeatureDescription: "We ensure all books are in good condition before reselling",
            recycleFeature: "Environmental Impact",
            recycleFeatureDescription: "Help reduce waste by giving your books a second chance",
            joinMovement: "Send Us Your Books",
            joinMovementDescription: "Start decluttering your shelves while helping others access affordable books",
            getStarted: "Get Started Now",
            environmentalImpact: "Environmental Impact",
            environmentalImpactDescription: "By sending us your unused books, you're helping reduce waste and making reading more accessible. Every book recycled is a step towards a more sustainable future.",
            
            // App Download
            getOurApp: "Get Our Mobile App",
            downloadAppDescription: "Download our app for a better reading experience on the go",
            downloadOn: "Download on the",
            getItOn: "GET IT ON",
            appFeatures: "App Features",
            feature1: "Read books offline",
            feature2: "Personalized recommendations",
            feature3: "Night reading mode",
            feature4: "Adjustable text size",
            scanQR: "Scan QR Code to Download",
            downloadNow: "Download Now",
            appVersion: "Latest Version",
            appSize: "App Size",
            rating: "Rating",
            reviews: "Reviews",
            
            // Books
            availableBooks: "Available Books",
            bookNotFound: "Book not found",
            bookNotFoundDesc: "The book you're looking for doesn't exist.",
            
            // Language
            switchToEnglish: "Switch to English",
            switchToPolish: "Switch to Polish",
            english: "English",
            polish: "Polish",
            
            // Book Info
            title: "Title",
            authors: "Authors",
            description: "Description",
            publisher: "Publisher",
            publishedDate: "Published Date",
            language: "Language",
            pages: "Pages",
            
            // Pagination
            previous: "Previous",
            next: "Next",
            page: "Page",
            of: "of",
            failedToFetchBooks: "Failed to fetch books. Please try again later.",

            // Actions
            buyInApp: "Buy in Mobile App",

            // Sort Options
            sortBy: "Sort by",
            default: "Default",
            priceLowToHigh: "Price: Low to High",
            priceHighToLow: "Price: High to Low",
            
            // Send Parcel
            sendParcel: "Send Books",
            loginToSendParcel: "Login to Send Books",
            makeParcel: "Create Shipment",
            submitting: "Submitting...",

            // Steps
            whatShouldYouDo: "What Should You Do?",
            step1Title: "Pack Your Books",
            step1Description: "Gather your books and pack them securely",
            step2Title: "Write Shipping Code",
            step2Description: "Receive your unique shipping code via email",
            step3Title: "Drop at Point",
            step3Description: "Drop your package at the nearest collection point",

            // Privacy Policy
            privacyPolicy: "Privacy Policy for Bookik",
            privacyIntro: "At Bookik, we believe in giving books a second life, allowing users to send their old or unused books to us, where we track, validate, and resell them at affordable prices. We understand the importance of privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and share your information in accordance with applicable laws, including the General Data Protection Regulation (GDPR).",
            introduction: "Introduction",
            infoWeCollect: "Information We Collect",
            infoWeCollectDesc: "We collect the following types of information to provide you with our services:",
            personalInfo: "Personal Information",
            personalInfoItems: {
                name: "Name",
                email: "Email address",
                phone: "Phone number",
                address: "Shipping address (for parcel delivery)"
            },
            bookTransactions: "Book Transactions",
            bookTransactionsItems: {
                details: "Details of books sent (e.g., title, author, condition)",
                received: "Books received and processed by Bookik",
                purchased: "Books purchased from our platform",
                sending: {
                    title: "Sending Books",
                    desc: "By sending books to Bookik, you acknowledge that you are transferring ownership of those books to us. Once we receive and validate the books, they will be listed for resale."
                },
                validation: {
                    title: "Book Validation",
                    desc: "Bookik reserves the right to assess the condition of the books sent and determine whether they are suitable for resale. Invalid or unsellable books will be returned or discarded, depending on the situation."
                },
                pricing: {
                    title: "Pricing and Sale",
                    desc: "The pricing of books for resale is determined by Bookik and may vary based on condition, demand, and other factors."
                }
            },
            walletData: "Wallet Data",
            walletDataItems: {
                balance: "Account balance",
                history: "Transaction history",
                refunds: "Refund details (if applicable)"
            },
            deviceData: "Device & Usage Data",
            deviceDataItems: {
                info: "Information about how you interact with our app, including IP addresses, device type, and browser information",
                cookies: "Cookies (if using the web version)"
            },
            howWeUseInfo: "How We Use the Information",
            howWeUseInfoItems: {
                process: "To process book shipments: We track and validate the books you send to us, ensuring they are listed and sold to others who wish to buy them.",
                wallet: "To process wallet transactions: We handle payments for book purchases and manage your account balance.",
                updates: "To communicate order/tracking updates: We keep you informed about the status of your books and shipments.",
                improve: "To improve app features: We analyze usage data to enhance user experience and functionality.",
                fraud: "To prevent fraud: We monitor transactions for potential fraudulent activity and protect both users and our platform."
            },
            dataSharing: "How We Share the Data",
            dataSharingDesc: "We share your data with the following third parties for the purposes outlined below:",
            dataSharingItems: {
                logistics: "Logistics Partners (e.g., InPost): To facilitate parcel delivery and tracking.",
                payment: "Payment Providers (e.g., Przelewy24): For processing wallet transactions and payments."
            },
            noDataSale: "We do not sell or share your data with third parties for advertising purposes.",
            dataSecurity: "Data Security",
            dataSecurityDesc: "We take your privacy seriously and have implemented the following security measures:",
            dataSecurityItems: {
                encryption: "Encryption: All personal and payment data is encrypted during transmission.",
                storage: "Secure Storage: Wallet balances and sensitive data are securely stored using industry-standard encryption techniques."
            },
            userRights: "User Rights",
            userRightsDesc: "You have the right to:",
            userRightsItems: {
                access: "Access, update, or delete your personal information.",
                copy: "Request a copy of your data.",
                optout: "Opt-out of non-essential tracking or data collection, such as cookies.",
                withdraw: "Withdraw your consent to data processing where applicable."
            },
            exerciseRights: "To exercise these rights, please contact us via the details provided below.",
            cookies: "Cookies & Tracking",
            cookiesDesc: "If you're using the web version of our platform, we may use cookies to enhance your experience. Cookies help us remember your preferences and track usage patterns. You can control cookie settings through your browser preferences.",
            contact: "Contact & Changes",
            contactDesc: "If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please contact us at:",
            email: "Email",
            policyUpdates: "We may update this Privacy Policy from time to time. Any changes will be communicated in advance, and the revised policy will be posted on our website.",

            // Terms & Conditions
            termsAndConditions: "Terms & Conditions for Bookik",
            termsIntro: "These Terms & Conditions govern your use of the Bookik platform (the \"Platform\"). By using the Platform, you agree to comply with these terms. If you do not agree to these terms, please refrain from using our services.",
            userResponsibilities: "User Responsibilities",
            userResponsibilitiesItems: {
                condition: {
                    title: "Book Condition",
                    desc: "Users must ensure that the books they send to Bookik are in good condition. We reserve the right to refuse books that do not meet our quality standards."
                },
                honesty: {
                    title: "Honesty in Transactions",
                    desc: "Users must provide accurate information regarding their books, including details about the book's condition, title, and author."
                },
                security: {
                    title: "Account Security",
                    desc: "Users are responsible for maintaining the confidentiality of their account credentials and for all activities that occur under their account."
                }
            },
            bookTransactionsTerms: "Book Transactions",
            walletRules: "Wallet Usage Rules",
            walletRulesItems: {
                balance: {
                    title: "Account Balance",
                    desc: "Your wallet balance represents the amount available for transactions on the Platform."
                },
                usage: {
                    title: "Use of Funds",
                    desc: "The balance in your wallet can only be used within the Bookik platform, such as for purchasing books or for specific services offered by Bookik."
                },
                refunds: {
                    title: "Refunds",
                    desc: "If applicable, refunds will be issued to the wallet balance. Refund policies are subject to the terms defined at the time of purchase."
                }
            },
            refundPolicy: "Refund Policy",
            refundPolicyItems: {
                purchases: {
                    title: "Refunds for Purchases",
                    desc: "Refunds for books purchased are only available if the book is damaged, incorrect, or not as described in the listing."
                },
                process: {
                    title: "Refund Process",
                    desc: "Refunds will be processed to the wallet balance and may take up to 7 business days to reflect."
                }
            },
            liability: "Limitation of Liability",
            liabilityDesc: "Bookik is not liable for any loss or damage resulting from the use of the Platform, including but not limited to errors in the book validation process, delivery issues, or third-party service failures.",
            changes: "Changes to Terms",
            changesDesc: "We reserve the right to update or modify these Terms & Conditions at any time. Any changes will be communicated to you, and your continued use of the Platform will signify your acceptance of the updated terms.",
            law: "Governing Law",
            lawDesc: "These Terms & Conditions shall be governed by and construed in accordance with the laws of Poland. Any disputes arising from or in connection with these terms will be subject to the exclusive jurisdiction of the courts in Poland."
        }
    },
    pl: {
        translation: {
            // General
            welcome: "Witamy",
            bookstore: "Księgarnia",
            home: "Strona główna",
            aboutUs: "O nas",
            
            // About Us Page
            giveBooksNewLife: "Daj Swoim Książkom Nowy Dom",
            missionStatement: "Wierzymy w udostępnianie książek wszystkim, dając jednocześnie Twoim starym książkom drugą szansę. Wyślij nam swoje ukochane książki, a my zadbamy o to, by znalazły nowe domy.",
            ourImpact: "Nasz Wpływ",
            booksShared: "Otrzymane Książki",
            activeCommunity: "Zadowoleni Klienci",
            treeSaved: "Uratowane Drzewa",
            howItWorks: "Jak To Działa",
            sustainabilityFeature: "Łatwe Przekazanie Książek",
            sustainabilityFeatureDescription: "Masz książki, których już nie potrzebujesz? Wyślij je do nas łatwo przez aplikację Bookik—pokrywamy nawet koszty dostawy!",
            communityFeature: "Zarabiaj na Darowiznach",
            communityFeatureDescription: "Otrzymaj 1zł za każdą podarowaną nam książkę",
            accessibilityFeature: "Kontrola Jakości",
            accessibilityFeatureDescription: "Sprawdzamy stan wszystkich książek przed ponowną sprzedażą",
            recycleFeature: "Wpływ na Środowisko",
            recycleFeatureDescription: "Pomóż zmniejszyć ilość odpadów, dając książkom drugą szansę",
            joinMovement: "Wyślij Nam Swoje Książki",
            joinMovementDescription: "Zacznij porządkować swoje półki, pomagając innym w dostępie do niedrogich książek",
            getStarted: "Rozpocznij Teraz",
            environmentalImpact: "Wpływ na Środowisko",
            environmentalImpactDescription: "Wysyłając nam swoje nieużywane książki, pomagasz zmniejszyć ilość odpadów i czynisz czytanie bardziej dostępnym. Każda poddana recyklingowi książka to krok w stronę bardziej zrównoważonej przyszłości.",
            
            // App Download
            getOurApp: "Pobierz Naszą Aplikację",
            downloadAppDescription: "Pobierz naszą aplikację, aby lepiej czytać w podróży",
            downloadOn: "Pobierz w",
            getItOn: "POBIERZ W",
            appFeatures: "Funkcje Aplikacji",
            feature1: "Czytaj książki offline",
            feature2: "Spersonalizowane rekomendacje",
            feature3: "Tryb nocny",
            feature4: "Regulowana wielkość tekstu",
            scanQR: "Zeskanuj kod QR, aby pobrać",
            downloadNow: "Pobierz Teraz",
            appVersion: "Najnowsza Wersja",
            appSize: "Rozmiar Aplikacji",
            rating: "Ocena",
            reviews: "Recenzje",
            
            // Books
            availableBooks: "Dostępne Książki",
            bookNotFound: "Nie znaleziono książki",
            bookNotFoundDesc: "Książka, której szukasz, nie istnieje.",
            
            // Language
            switchToEnglish: "Przełącz na angielski",
            switchToPolish: "Przełącz na polski",
            english: "Angielski",
            polish: "Polski",
            
            // Book Info
            title: "Tytuł",
            authors: "Autorzy",
            description: "Opis",
            publisher: "Wydawca",
            publishedDate: "Data wydania",
            language: "Język",
            pages: "Strony",
            
            // Pagination
            previous: "Poprzednia",
            next: "Następna",
            page: "Strona",
            of: "z",
            failedToFetchBooks: "Nie udało się pobrać książek. Spróbuj ponownie później.",

            // Actions
            buyInApp: "Kup w Aplikacji Mobilnej",

            // Sort Options
            sortBy: "Sortuj według",
            default: "Domyślnie",
            priceLowToHigh: "Cena: od najniższej",
            priceHighToLow: "Cena: od najwyższej",
            
            // Send Parcel
            sendParcel: "Wyślij Książki",
            loginToSendParcel: "Zaloguj się, aby wysłać książki",
            makeParcel: "Utwórz Przesyłkę",
            submitting: "Wysyłanie...",

            // Steps
            whatShouldYouDo: "Co Powinieneś Zrobić?",
            step1Title: "Spakuj Książki",
            step1Description: "Zbierz swoje książki i bezpiecznie je zapakuj",
            step2Title: "Wpisz Kod Wysyłki",
            step2Description: "Otrzymaj unikalny kod wysyłki na email",
            step3Title: "Zostaw w Punkcie",
            step3Description: "Zostaw paczkę w najbliższym punkcie odbioru",

            // Privacy Policy
            privacyPolicy: "Polityka Prywatności Bookik",
            privacyIntro: "W Bookik wierzymy w dawanie książkom drugiego życia, umożliwiając użytkownikom wysyłanie nam swoich starych lub nieużywanych książek, które śledzimy, weryfikujemy i odsprzedajemy po przystępnych cenach. Rozumiemy znaczenie prywatności i zobowiązujemy się do ochrony Twoich danych osobowych. Ta Polityka Prywatności wyjaśnia, w jaki sposób gromadzimy, wykorzystujemy i udostępniamy Twoje informacje zgodnie z obowiązującymi przepisami, w tym z Ogólnym Rozporządzeniem o Ochronie Danych (RODO).",
            introduction: "Wprowadzenie",
            infoWeCollect: "Informacje, które gromadzimy",
            infoWeCollectDesc: "Gromadzimy następujące rodzaje informacji, aby świadczyć nasze usługi:",
            personalInfo: "Dane Osobowe",
            personalInfoItems: {
                name: "Imię i nazwisko",
                email: "Adres e-mail",
                phone: "Numer telefonu",
                address: "Adres wysyłki (do dostawy paczek)"
            },
            bookTransactions: "Transakcje Książkowe",
            bookTransactionsItems: {
                details: "Szczegóły wysłanych książek (np. tytuł, autor, stan)",
                received: "Książki otrzymane i przetworzone przez Bookik",
                purchased: "Książki zakupione z naszej platformy",
                sending: {
                    title: "Wysyłanie Książek",
                    desc: "Wysyłając książki do Bookik, potwierdzasz, że przenosisz na nas własność tych książek. Po otrzymaniu i weryfikacji książek, zostaną one wystawione na sprzedaż."
                },
                validation: {
                    title: "Weryfikacja Książek",
                    desc: "Bookik zastrzega sobie prawo do oceny stanu wysłanych książek i określenia, czy nadają się do odsprzedaży. Nieprawidłowe lub nie nadające się do sprzedaży książki zostaną zwrócone lub utylizowane, w zależności od sytuacji."
                },
                pricing: {
                    title: "Cennik i Sprzedaż",
                    desc: "Ceny książek przeznaczonych do odsprzedaży są ustalane przez Bookik i mogą się różnić w zależności od stanu, popytu i innych czynników."
                }
            },
            walletData: "Dane Portfela",
            walletDataItems: {
                balance: "Stan konta",
                history: "Historia transakcji",
                refunds: "Szczegóły zwrotów (jeśli dotyczy)"
            },
            deviceData: "Dane Urządzenia i Użytkowania",
            deviceDataItems: {
                info: "Informacje o tym, jak korzystasz z naszej aplikacji, w tym adresy IP, typ urządzenia i informacje o przeglądarce",
                cookies: "Pliki cookie (w przypadku korzystania z wersji internetowej)"
            },
            howWeUseInfo: "Jak Wykorzystujemy Informacje",
            howWeUseInfoItems: {
                process: "Do przetwarzania wysyłek książek: Śledzimy i weryfikujemy książki, które nam wysyłasz, zapewniając ich wystawienie i sprzedaż innym zainteresowanym.",
                wallet: "Do przetwarzania transakcji portfela: Obsługujemy płatności za zakupy książek i zarządzamy stanem konta.",
                updates: "Do komunikacji o statusie zamówień/śledzenia: Informujemy Cię o statusie Twoich książek i przesyłek.",
                improve: "Do ulepszania funkcji aplikacji: Analizujemy dane użytkowania, aby poprawić doświadczenie użytkownika i funkcjonalność.",
                fraud: "Do zapobiegania oszustwom: Monitorujemy transakcje pod kątem potencjalnych działań oszukańczych i chronimy zarówno użytkowników, jak i naszą platformę."
            },
            dataSharing: "Jak Udostępniamy Dane",
            dataSharingDesc: "Udostępniamy Twoje dane następującym stronom trzecim w określonych celach:",
            dataSharingItems: {
                logistics: "Partnerzy Logistyczni (np. InPost): Do realizacji dostaw i śledzenia przesyłek.",
                payment: "Dostawcy Płatności (np. Przelewy24): Do przetwarzania transakcji portfela i płatności."
            },
            noDataSale: "Nie sprzedajemy ani nie udostępniamy Twoich danych stronom trzecim w celach reklamowych.",
            dataSecurity: "Bezpieczeństwo Danych",
            dataSecurityDesc: "Poważnie traktujemy Twoją prywatność i wdrożyliśmy następujące środki bezpieczeństwa:",
            dataSecurityItems: {
                encryption: "Szyfrowanie: Wszystkie dane osobowe i płatnicze są szyfrowane podczas transmisji.",
                storage: "Bezpieczne Przechowywanie: Salda portfela i wrażliwe dane są bezpiecznie przechowywane przy użyciu standardowych technik szyfrowania."
            },
            userRights: "Prawa Użytkownika",
            userRightsDesc: "Masz prawo do:",
            userRightsItems: {
                access: "Dostępu, aktualizacji lub usunięcia swoich danych osobowych.",
                copy: "Żądania kopii swoich danych.",
                optout: "Rezygnacji z nieistotnego śledzenia lub zbierania danych, takich jak pliki cookie.",
                withdraw: "Wycofania zgody na przetwarzanie danych, jeśli ma to zastosowanie."
            },
            exerciseRights: "Aby skorzystać z tych praw, skontaktuj się z nami poprzez podane poniżej dane kontaktowe.",
            cookies: "Pliki Cookie i Śledzenie",
            cookiesDesc: "Jeśli korzystasz z wersji internetowej naszej platformy, możemy używać plików cookie, aby ulepszyć Twoje doświadczenie. Pliki cookie pomagają nam zapamiętać Twoje preferencje i śledzić wzorce użytkowania. Możesz kontrolować ustawienia plików cookie poprzez preferencje przeglądarki.",
            contact: "Kontakt i Zmiany",
            contactDesc: "Jeśli masz pytania dotyczące tej Polityki Prywatności lub chcesz skorzystać ze swoich praw do prywatności, skontaktuj się z nami pod adresem:",
            email: "E-mail",
            policyUpdates: "Możemy aktualizować tę Politykę Prywatności od czasu do czasu. Wszelkie zmiany będą komunikowane z wyprzedzeniem, a zaktualizowana polityka zostanie opublikowana na naszej stronie internetowej.",

            // Terms & Conditions
            termsAndConditions: "Regulamin Bookik",
            termsIntro: "Niniejszy Regulamin określa zasady korzystania z platformy Bookik (\"Platforma\"). Korzystając z Platformy, zgadzasz się przestrzegać tych warunków. Jeśli nie zgadzasz się z tymi warunkami, prosimy o powstrzymanie się od korzystania z naszych usług.",
            userResponsibilities: "Obowiązki Użytkownika",
            userResponsibilitiesItems: {
                condition: {
                    title: "Stan Książek",
                    desc: "Użytkownicy muszą upewnić się, że książki wysyłane do Bookik są w dobrym stanie. Zastrzegamy sobie prawo do odmowy przyjęcia książek, które nie spełniają naszych standardów jakości."
                },
                honesty: {
                    title: "Uczciwość w Transakcjach",
                    desc: "Użytkownicy muszą podawać dokładne informacje dotyczące swoich książek, w tym szczegóły dotyczące stanu książki, tytułu i autora."
                },
                security: {
                    title: "Bezpieczeństwo Konta",
                    desc: "Użytkownicy są odpowiedzialni za zachowanie poufności swoich danych logowania i za wszystkie działania, które odbywają się na ich koncie."
                }
            },
            bookTransactionsTerms: "Transakcje Książkowe",
            walletRules: "Zasady Korzystania z Portfela",
            walletRulesItems: {
                balance: {
                    title: "Stan Konta",
                    desc: "Saldo Twojego portfela reprezentuje kwotę dostępną do transakcji na Platformie."
                },
                usage: {
                    title: "Wykorzystanie Środków",
                    desc: "Saldo w Twoim portfelu może być wykorzystane tylko w ramach platformy Bookik, na przykład do zakupu książek lub określonych usług oferowanych przez Bookik."
                },
                refunds: {
                    title: "Zwroty",
                    desc: "W stosownych przypadkach zwroty będą dokonywane na saldo portfela. Zasady zwrotów podlegają warunkom określonym w momencie zakupu."
                }
            },
            refundPolicy: "Polityka Zwrotów",
            refundPolicyItems: {
                purchases: {
                    title: "Zwroty za Zakupy",
                    desc: "Zwroty za zakupione książki są dostępne tylko wtedy, gdy książka jest uszkodzona, nieprawidłowa lub niezgodna z opisem w ogłoszeniu."
                },
                process: {
                    title: "Proces Zwrotu",
                    desc: "Zwroty będą przetwarzane na saldo portfela i mogą potrwać do 7 dni roboczych, aby się pojawić."
                }
            },
            liability: "Ograniczenie Odpowiedzialności",
            liabilityDesc: "Bookik nie ponosi odpowiedzialności za jakiekolwiek straty lub szkody wynikające z korzystania z Platformy, w tym między innymi błędy w procesie weryfikacji książek, problemy z dostawą lub awarie usług stron trzecich.",
            changes: "Zmiany w Regulaminie",
            changesDesc: "Zastrzegamy sobie prawo do aktualizacji lub modyfikacji niniejszego Regulaminu w dowolnym momencie. Wszelkie zmiany zostaną zakomunikowane, a dalsze korzystanie z Platformy będzie oznaczać akceptację zaktualizowanych warunków.",
            law: "Prawo Właściwe",
            lawDesc: "Niniejszy Regulamin podlega prawu polskiemu i zgodnie z nim będzie interpretowany. Wszelkie spory wynikające z lub związane z tymi warunkami będą podlegać wyłącznej jurysdykcji sądów w Polsce."
        }
    }
};

i18n
    .use(initReactI18next)
    .init({
        resources,
        lng: "pl",
        fallbackLng: "pl",
        interpolation: {
            escapeValue: false
        }
    });

export default i18n; 